/**
 * LOOKING GLASS — Immich Archive Configuration
 * Single source of truth for the Immich bridge endpoint + API key.
 * Stored in localStorage as `lg-immich-config`.
 *
 * Follows the exact loadXConfig()/saveXConfig() pattern established by
 * src/utils/aiConfig.js — this repo has NO import.meta.env usage and
 * vite.config.js declares no envPrefix, so a Vite env var would silently
 * be undefined. localStorage is the real convention here.
 *
 * baseUrl defaults to the SAME-ORIGIN serverless proxy `/api/immich`
 * so the client can never trip a mixed-content block (HTTPS page calling
 * http://) or a CORS wall. Users on a LAN can override with an absolute
 * http:// URL, which is why resolveBaseUrl() exists.
 */
const STORAGE_KEY = 'lg-immich-config';

// Same-origin default. The backend mounts the whole archive API under this
// prefix (GET /api/immich/assets, /api/immich/projects, /api/immich/tags).
export const DEFAULT_BASE_URL = '/api/immich';

const DEFAULT_CONFIG = { baseUrl: DEFAULT_BASE_URL, apiKey: '' };

// ── Minimal obfuscation (NOT real encryption) ───────────────────────────────
// Mirrors aiConfig.js so the two config modules read identically.
const obfuscate = (key) => {
  try { return btoa(key.split('').reverse().join('')); } catch { return ''; }
};
const deobfuscate = (enc) => {
  try { return atob(enc).split('').reverse().join(''); } catch { return ''; }
};

/** Strip trailing slashes so path joins never double up. */
export function normalizeBaseUrl(url) {
  if (!url) return '';
  return String(url).trim().replace(/\/+$/, '');
}

export function loadImmichConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CONFIG };
    const parsed = JSON.parse(raw);
    return {
      baseUrl: normalizeBaseUrl(parsed.baseUrl) || DEFAULT_BASE_URL,
      apiKey: parsed.apiKey ? deobfuscate(parsed.apiKey) : '',
    };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

export function saveImmichConfig({ baseUrl, apiKey }) {
  const clean = normalizeBaseUrl(baseUrl) || DEFAULT_BASE_URL;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    baseUrl: clean,
    apiKey: apiKey ? obfuscate(apiKey) : '',
  }));
  return { baseUrl: clean, apiKey };
}

export function clearImmichConfig() {
  localStorage.removeItem(STORAGE_KEY);
  return { ...DEFAULT_CONFIG };
}

/**
 * Build an absolute request URL for a given Immich API path.
 * `path` is relative to the configured baseUrl, e.g. '/assets'.
 * Never returns undefined — callers get a fetchable string.
 */
export function resolveApiUrl(path, overrideBaseUrl) {
  const cfg = loadImmichConfig();
  const base = normalizeBaseUrl(overrideBaseUrl || cfg.baseUrl) || DEFAULT_BASE_URL;
  const suffix = path && path !== '/' ? String(path) : '';
  return `${base}${suffix.startsWith('/') ? suffix : `/${suffix}`}`;
}

/**
 * Byte-serving URLs. These are the ONLY image sources the browser can use —
 * `preview_path` / `thumb_path` on an asset are absolute local filesystem
 * paths on the machine running the API server and are meaningless (and
 * useless) to a browser. Always go through these.
 */
export function previewUrl(asset, baseUrl) {
  if (!asset?.id) return '';
  return resolveApiUrl(`/assets/${asset.id}/preview`, baseUrl);
}

export function thumbUrl(asset, baseUrl) {
  if (!asset?.id) return '';
  return resolveApiUrl(`/assets/${asset.id}/thumb`, baseUrl);
}

/**
 * A one-request connectivity probe. Never throws — always resolves
 * { ok:true, total } or { ok:false, message } with a human-readable reason,
 * because the two failure modes users actually hit are (a) the backend is not
 * running / route not mounted (404 on /assets) and (b) a bad API key (401).
 */
export async function testImmichConnection({ baseUrl, apiKey } = {}) {
  try {
    const url = resolveApiUrl('/assets', baseUrl);
    const headers = {};
    const key = apiKey !== undefined ? apiKey : loadImmichConfig().apiKey;
    if (key) headers['x-api-key'] = key;

    const resp = await fetch(`${url}?limit=1`, { headers });
    if (resp.ok) {
      let total = null;
      try {
        const data = await resp.json();
        total = data?.meta?.total ?? null;
      } catch { /* body not JSON — connection is still fine */ }
      return { ok: true, total };
    }

    if (resp.status === 401 || resp.status === 403) {
      return { ok: false, status: resp.status, message: 'Invalid or missing API key (401). Check the key and try again.' };
    }
    if (resp.status === 404) {
      return { ok: false, status: 404, message: `No Immich API at ${url} (404). Is the backend running and is the archive mounted at /api/immich?` };
    }
    return { ok: false, status: resp.status, message: `Upstream error ${resp.status}${resp.statusText ? `: ${resp.statusText}` : ''}` };
  } catch (err) {
    return { ok: false, status: 0, message: err?.message || String(err) };
  }
}